import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, CalendarDays, Users, Settings, LogOut, Menu, X, Clock, UserRound } from "lucide-react";
import { Logo, LangToggle, ThemeToggle } from "@/components/atlas/Shared";
import { NotificationBell } from "@/components/atlas/NotificationBell";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";

const ADMIN_NAV = [
  ["/admin", "nav.dashboard", LayoutDashboard, true],
  ["/admin/events", "nav.events", CalendarDays],
  ["/admin/employees", "nav.employees", Users],
  ["/admin/timesheets", "nav.timesheets", Clock],
  ["/admin/settings", "nav.settings", Settings],
];

const NavItems = ({ items, onNavigate }) => {
  const { t } = useLang();
  return items.map(([to, label, Icon, end]) => (
    <NavLink
      key={to}
      to={to}
      end={end}
      onClick={onNavigate}
      data-testid={`nav-${label.split(".")[1]}`}
      className={({ isActive }) =>
        `press flex items-center gap-3 mx-3 px-4 py-2.5 rounded-full text-sm ${isActive ? "text-[#D9B872] bg-[#C5A059]/[0.12]" : "text-white/55 hover:text-white hover:bg-white/[0.04]"}`
      }
    >
      <Icon className="w-4 h-4" strokeWidth={1.5} />
      {t(label)}
    </NavLink>
  ));
};

export default function StaffLayout() {
  const { user, signOut } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const items = user.is_staff ? [...ADMIN_NAV, ["/app", "nav.mySpace", UserRound]] : ADMIN_NAV;
  const logout = () => {
    signOut();
    navigate("/login");
  };

  const sidebar = (
    <div className="dark-zone flex flex-col h-full bg-[#08080A] text-white border-r border-white/[0.06]">
      <div className="px-6 py-7"><Logo light /></div>
      <div className="gold-rule mx-6 mb-6 opacity-60" />
      <nav className="flex flex-col gap-1"><NavItems items={items} onNavigate={() => setOpen(false)} /></nav>
      <div className="mt-auto p-6 space-y-4">
        <div className="flex items-center gap-2"><LangToggle light /><ThemeToggle /></div>
        <div className="text-xs text-white/50">
          <div className="text-white/90 text-sm" data-testid="current-user-name">{user.name}</div>
          <div>Administration</div>
        </div>
        <button onClick={logout} data-testid="logout-btn" className="press flex items-center gap-2 text-xs text-white/60 hover:text-[#C5A059]">
          <LogOut className="w-3.5 h-3.5" /> {t("c.logout")}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen lg:pl-64">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 z-30">{sidebar}</aside>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64">{sidebar}</div>
          <button className="flex-1 bg-black/50" onClick={() => setOpen(false)} data-testid="mobile-nav-close" aria-label="close" />
        </div>
      )}
      <header className="sticky top-0 z-20 backdrop-blur-md bg-[#0B0B0C]/85 border-b border-white/[0.06]">
        <div className="flex items-center justify-between px-5 sm:px-8 lg:px-12 h-14">
          <button className="lg:hidden p-2 -ml-2" onClick={() => setOpen(true)} data-testid="mobile-nav-open">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div className="lg:hidden"><Logo compact /></div>
          <div className="hidden lg:block eyebrow">{t("app.tagline")}</div>
          <NotificationBell />
        </div>
      </header>
      <main className="px-5 sm:px-8 lg:px-12 py-8 lg:py-10 max-w-[1600px]">
        <Outlet />
      </main>
    </div>
  );
}
